import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertCircle, Check } from 'lucide-react';
import { socialPlatformLabel, type CopyDestinationResult, type PostGroupDestination } from '@mg/shared';
import { api, actionErrorText } from '../../lib/api';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { cn } from '../../lib/cn';

/**
 * Copy this destination's content onto others.
 *
 * ONE-SHOT, AND IT SAYS SO. Nothing links the destinations afterwards; this overwrites what they
 * hold right now. The alternative — a shared draft the destinations track — is a promise the
 * approval model takes away one destination at a time, since a post stops accepting content edits
 * the moment it is approved.
 *
 * Nothing is pre-ticked, and each destination is named. Content has no version history and no trash
 * of its own, so an overwrite here is permanent — this is the one control in the composer that can
 * destroy work with no way back, and it should feel like it.
 */
export function CopyToDestinationsModal({
  open,
  onClose,
  fromPostId,
  destinations,
  onCopied,
}: {
  open: boolean;
  onClose: () => void;
  fromPostId: string;
  /** Every destination in the set, including the source — filtered out below. */
  destinations: PostGroupDestination[];
  onCopied: () => void;
}) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const [results, setResults] = useState<CopyDestinationResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const others = destinations.filter((d) => d.postId !== fromPostId);
  // A published destination is our record of what went out: its content can no longer change.
  const locked = (d: PostGroupDestination) => d.publishedAt != null;
  // What will be overwritten: ticked, and still open (one may be published while this is open).
  const chosen = others.filter((d) => picked.has(d.postId) && !locked(d)).map((d) => d.postId);

  const copy = useMutation({
    mutationFn: () => api.copyToDestinations(fromPostId, chosen),
    onSuccess: (rows) => {
      setResults(rows);
      // Refresh regardless of refusals: some destinations may have changed even when others didn't.
      onCopied();
    },
    onError: (err) => setError(actionErrorText(err, 'Could not copy to those destinations.')),
  });

  const close = () => {
    setPicked(new Set());
    setResults(null);
    setError(null);
    onClose();
  };

  const toggle = (d: PostGroupDestination) => {
    if (locked(d)) return;
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(d.postId)) next.delete(d.postId);
      else next.add(d.postId);
      return next;
    });
  };

  const label = (d: PostGroupDestination) =>
    `${d.platform ? socialPlatformLabel[d.platform] : 'Destination'}${d.accountName ? ` · ${d.accountName}` : ''}`;

  return (
    <Modal
      open={open}
      onClose={close}
      title="Copy to other destinations"
      footer={
        results ? (
          <Button onClick={close}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={close}>
              Cancel
            </Button>
            <Button disabled={chosen.length === 0} loading={copy.isPending} onClick={() => copy.mutate()}>
              Overwrite {chosen.length || ''} destination{chosen.length === 1 ? '' : 's'}
            </Button>
          </>
        )
      }
    >
      {results ? (
        <ul className="space-y-2">
          {results.map((r) => {
            const d = destinations.find((x) => x.postId === r.postId);
            return (
              <li
                key={r.postId}
                className={cn(
                  'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-sm',
                  r.ok ? 'border-line' : 'border-amber-200 dark:border-warning-dark/30 bg-amber-50/60 dark:bg-warning-dark-tint/60',
                )}
              >
                {r.ok ? (
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-success dark:text-success-dark" aria-hidden />
                ) : (
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-warning-dark" aria-hidden />
                )}
                <span className="min-w-0">
                  <span className="block font-medium text-ink">{d ? label(d) : 'Destination'}</span>
                  {/* The destination's OWN reason, not a generic failure — "X takes up to 4 images
                      per post" tells the user what to change; "could not copy" does not. */}
                  <span className={cn('block text-xs', r.ok ? 'text-ink-muted' : 'text-amber-800 dark:text-warning-dark')}>
                    {r.ok ? 'Updated' : r.reason}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-ink-label">
            This replaces what those destinations currently hold with the content of the destination
            you have open. It happens once — they do not stay linked afterwards, and there is no undo.
          </p>
          {others.length === 0 ? (
            <p className="text-sm text-ink-muted">This idea has no other destinations yet.</p>
          ) : (
            <ul className="space-y-1.5">
              {others.map((d) => (
                <li key={d.postId}>
                  <label
                    className={cn(
                      'flex items-center gap-2.5 rounded-lg border border-line px-3 py-2.5 text-sm',
                      locked(d) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-hover',
                    )}
                  >
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-line-input"
                      checked={chosen.includes(d.postId)}
                      disabled={locked(d)}
                      onChange={() => toggle(d)}
                    />
                    <span className="font-medium text-ink">{label(d)}</span>
                    {/* A published destination is our record of what went out: it is never offered,
                        and the API refuses it in every approval mode. */}
                    {locked(d) && (
                      <span className="ml-auto text-xs text-ink-faint">already published</span>
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
          {error && <p className="text-sm text-red-600 dark:text-error-dark">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
